import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import TopNav from "../components/TopNav";
import { ShieldCheck, FileDown, CheckCircle2, Clock, Circle, ExternalLink } from "lucide-react";

const PUBLIC_API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const META = {
  implemented: { color: "var(--green)", label: "Implemented", icon: CheckCircle2 },
  in_progress: { color: "var(--amber)", label: "In Progress", icon: Clock },
  planned: { color: "var(--red)", label: "Planned", icon: Circle },
};

export default function Security() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const isLeader = user?.role === "leader" || user?.role === "org_admin" || user?.role === "admin";

  useEffect(() => {
    if (!isLeader) return;
    api.get("/me/security/posture")
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || e.message));
  }, [isLeader]);

  if (!isLeader) {
    return (
      <div className="min-h-screen bg-[var(--bg)]">
        <TopNav />
        <main className="max-w-7xl mx-auto px-6 py-10">
          <div className="flat-card p-8 border border-[var(--amber)]" data-testid="sec-no-access">
            <div className="font-display uppercase text-lg">Leader access only</div>
            <p className="text-sm text-[var(--text-2)] mt-2">Ask your squadron leader for access.</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg)]">
      <TopNav />
      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="overline">// INTERNAL_SECURITY_POSTURE</div>
            <h1 className="font-display font-bold text-4xl uppercase mt-2">Security Posture</h1>
            <p className="text-sm text-[var(--text-2)] mt-2 max-w-2xl">
              Live state of your security controls. Use this in sales calls and procurement reviews — every claim here is sourced from <a href="/trust" className="text-[var(--cyan)] underline">/trust</a>.
            </p>
          </div>
          <div className="flex gap-2">
            <a href={`${PUBLIC_API}/security/pack.pdf`} target="_blank" rel="noreferrer"
               className="btn-primary inline-flex items-center gap-2 text-xs" data-testid="sec-download-pack">
              <FileDown size={14} /> Download Pack PDF
            </a>
            <a href="/trust" target="_blank" rel="noreferrer"
               className="btn-ghost inline-flex items-center gap-2 text-xs" data-testid="sec-open-trust">
              <ExternalLink size={12} /> Public Trust Page
            </a>
          </div>
        </div>

        {err && <div className="mt-6 font-mono text-xs text-[var(--red)] border border-[var(--red)] p-3" data-testid="sec-error">{err}</div>}

        {data && (
          <>
            <div className="mt-8 grid grid-cols-2 md:grid-cols-5 gap-px bg-[var(--border)] border border-[var(--border)]" data-testid="sec-summary">
              {[
                ["TOTAL", data.summary.total, "var(--text-1)"],
                ["IMPLEMENTED", data.summary.implemented, "var(--green)"],
                ["IN PROGRESS", data.summary.in_progress, "var(--amber)"],
                ["PLANNED", data.summary.planned, "var(--red)"],
                ["COVERAGE", `${data.summary.coverage_pct}%`, "var(--cyan)"],
              ].map(([k, v, color]) => (
                <div key={k} className="bg-[var(--bg)] p-4">
                  <div className="overline">{k}</div>
                  <div className="font-mono text-2xl mt-1" style={{ color }}>{v}</div>
                </div>
              ))}
            </div>
            <div className="font-mono text-[10px] text-[var(--text-2)] mt-2">// as of {data.summary.as_of}</div>

            <div className="mt-10 space-y-8">
              {data.domains.map((d) => (
                <div key={d.domain} className="flat-card p-5" data-testid={`sec-domain-${d.domain.replace(/\s+/g, "-").toLowerCase()}`}>
                  <div className="flex items-center gap-2 mb-3 border-b border-[var(--border)] pb-2">
                    <ShieldCheck size={16} color="var(--cyan)" strokeWidth={1.5} />
                    <h2 className="font-display font-bold uppercase tracking-widest">{d.domain}</h2>
                    <span className="font-mono text-xs text-[var(--text-2)]">// {d.controls.length}</span>
                  </div>
                  <div className="divide-y divide-[var(--border)]">
                    {d.controls.map((c) => {
                      const meta = META[c.status] || META.planned;
                      const I = meta.icon;
                      return (
                        <div key={c.id} className="py-3 flex items-start justify-between gap-4 flex-wrap" data-testid={`sec-control-${c.id}`}>
                          <div className="min-w-0 flex-1">
                            <div className="font-mono text-xs">
                              <span className="text-[var(--cyan)]">{c.id}</span> · <span className="text-white">{c.name}</span>
                            </div>
                            <div className="font-mono text-[10px] text-[var(--text-2)] mt-1">{c.code_ref}</div>
                          </div>
                          <span className="inline-flex items-center gap-1 font-mono text-[10px] tracking-widest uppercase px-2 py-1 border"
                                style={{ color: meta.color, borderColor: meta.color }}>
                            <I size={10} /> {meta.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
